/**
 * MessageReactionButton — hover-reveal reaction toggle for a chat message.
 *
 * The ONE shared "like" / "mark done" affordance consumed by every Rooms
 * message surface (client portal `RoomsWorkspace`, Ops React SPA, medspa
 * staff web app). Each surface supplies its own data-layer wiring via
 * `onToggle`; this component owns only the visual toggle + a11y contract.
 *
 * Icons are self-contained inline SVGs so `@plerity/ui-core` keeps zero icon
 * dependencies (it ships only `clsx` + `class-variance-authority`).
 *
 * Colors are LITERAL Tailwind gray/blue/emerald ramp classes with a `dark:`
 * pair for every color — never `var(--color-*)` / `text-brand-*` /
 * `bg-surface-*`. Those emit CSS vars with no fallback and are stripped by
 * the React SPA apps' Tailwind v4 build, so this button must render
 * identically across the Astro portal, the Ops SPA, and the medspa app.
 *
 * @layer atom
 * @package @plerity/ui-core
 */
import { memo } from "react";
import { cn } from "../utils/cn";
import type { MessageReactionButtonProps, ReactionTone } from "./MessageReactionButton.types";
import { DONE_EMOJI, LIKE_EMOJI } from "./MessageReactionButton.types";

// Full literal Tailwind class strings per tone — NEVER interpolate the tint
// into a template string (`text-${tint}-600`). Tailwind's scanner only picks
// up complete class-name literals it can find in source; a dynamically
// constructed name is a phantom class that silently drops from the compiled
// CSS on the React SPA Tailwind v4 build (see `ApprovalBadge`'s
// `STATUS_CONFIG` for the same pattern).
const TONE_CLASSES: Record<ReactionTone, { active: string; inactiveHover: string }> = {
  like: {
    active: "text-blue-600 dark:text-blue-400",
    inactiveHover: "hover:text-blue-600 dark:hover:text-blue-400",
  },
  done: {
    active: "text-emerald-600 dark:text-emerald-400",
    inactiveHover: "hover:text-emerald-600 dark:hover:text-emerald-400",
  },
};

function ThumbUpIcon({ active }: { active: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5"
      aria-hidden="true"
      fill={active ? "currentColor" : "none"}
    >
      <path d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H14.23c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904M14.25 9h2.256M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path d="m4.5 12.75 6 6 9-13.5" />
    </svg>
  );
}

function resolveIcon(emoji: string, active: boolean) {
  if (emoji === LIKE_EMOJI) return <ThumbUpIcon active={active} />;
  if (emoji === DONE_EMOJI) return <CheckIcon />;
  return (
    <span aria-hidden="true" className="text-sm leading-none">
      {emoji}
    </span>
  );
}

export const MessageReactionButton = memo(function MessageReactionButton({
  emoji,
  active,
  onToggle,
  labelInactive,
  labelActive,
  tone = "like",
  icon,
  alwaysVisible = false,
  disabled,
  className,
  testId,
}: MessageReactionButtonProps) {
  const toneClasses = TONE_CLASSES[tone];
  const label = active ? labelActive : labelInactive;

  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={label}
      title={label}
      disabled={disabled}
      data-testid={testId}
      onClick={() => onToggle(emoji, active)}
      className={cn(
        "inline-flex items-center justify-center rounded-md p-1 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
        active
          ? cn(toneClasses.active, "opacity-100")
          : cn(
              "text-gray-400",
              "dark:text-gray-500",
              toneClasses.inactiveHover,
              !alwaysVisible && "opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
            ),
        className
      )}
    >
      {icon ?? resolveIcon(emoji, active)}
    </button>
  );
});

MessageReactionButton.displayName = "MessageReactionButton";
